import {
  Component,
  computed,
  effect,
  inject,
  signal,
  DestroyRef,
  viewChild,
  ElementRef,
  ViewChild
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  FullCalendarComponent,
  CalendarEvent,
  GenericDropdownComponent,
  DropdownData,
  HeaderService,
  AbilityService
} from '@zayan-erp/shared';
import { toSignal } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { of, tap } from 'rxjs';
import { SalesService } from '../../services/sales.service';
import { SalesApsService } from '@zayan-erp/sales';
import {
  HolidayCalendarSummary,
  HolidayCalendarSummaryParams,
  LeaveCalendarEvent,
  LotClosingDate
} from '../../models/holiday-calendar.models';
import { Deal } from '../../models/deal.models';
import { LinkedSalesAgentInfo } from '../../models/linked-sales-agent.models';
import { SalesOffice, SalesOfficeService } from '@zayan-erp/home-building';
import { LookupsService, NgbModalService } from '@zayan-erp/shared';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SvgIconsComponent } from '@zayan-erp/shared';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { format } from 'date-fns/format';

@Component({
  selector: 'app-sales-calendar',
  standalone: true,
  imports: [
    FullCalendarComponent,
    GenericDropdownComponent,
    FormsModule,
    NgSelectModule,
    SvgIconsComponent,
    DatePickerModule,
    FormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './sales-calendar.component.html',
  styleUrl: './sales-calendar.component.scss'
})
export class SalesCalendarComponent {
  @ViewChild('dealDetailsModal') dealDetailsModal!: ElementRef;
  readonly #modalService = inject(NgbModal);
  readonly #ngbModalService = inject(NgbModalService);
  readonly #salesService = inject(SalesService);
  readonly #salesApsService = inject(SalesApsService);
  readonly #salesOfficeService = inject(SalesOfficeService);
  readonly #lookupsService = inject(LookupsService);
  readonly #headerService = inject(HeaderService);
  readonly #abilityService = inject(AbilityService);
  readonly ability = this.#abilityService.ability;
  readonly selectedProject = signal<string[] | null>(null);
  readonly currentYear = signal<number>(new Date().getFullYear());
  readonly projectColorMap = new Map<string, string>();
  readonly selectedDealId = signal<string | null>(null);

  allProjectIds = signal<string[]>([]);
  selectedSalesOfficeId = signal<string | null>(null);
  selectedOperatingUnitId = signal<string | null>(null);
  viewStartDate = signal<string>('');
  viewEndDate = signal<string>('');

  ngOnInit(): void {
    this.#headerService.setSearchConfig({
      showSearch: false,
      showIcon: false,
      placeholder: '',
      showSearchResults: false,
      search: () => of([]),
      handleResult: () => {}
    });
  }
  readonly form = new FormGroup({
    closingDate: new FormControl<Date>(new Date())
  });

  readonly operatingUnits = toSignal(this.#lookupsService.readOperatingUnits(), { initialValue: [] as DropdownData[] });
  readonly salesOfficeResource = rxResourceFixed({
    params: () => ({ operatingUnitId: this.selectedOperatingUnitId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.operatingUnitId) {
        return of([]);
      }
      return this.#salesOfficeService
        .getSalesOfficeByOperatingUnits(params.operatingUnitId)
        .pipe(tap((res: SalesOffice[]) => this.allProjectIds.set(res.flatMap(p => p.projects.map(p => p.id)))));
    }
  });

  readonly groupedProjects = computed(() => {
    const salesOffices = this.salesOfficeResource.value() ?? [];
    const grouped: { id: string; projectName: string; salesOfficeName: string }[] = [];

    salesOffices.forEach(office => {
      office.projects.forEach(project => {
        grouped.push({
          id: project.id,
          projectName: project.name || project.code || '',
          salesOfficeName: office.name
        });
      });
    });

    return grouped;
  });
  readonly #projectsResource = rxResourceFixed({
    params: () => ({ salesOfficeId: this.selectedSalesOfficeId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.salesOfficeId) {
        return of([]);
      }
      return this.#salesOfficeService.getSalesOfficeProjects(params.salesOfficeId);
    }
  });
  readonly filteredProjects = computed(() => {
    const projects = this.#projectsResource.value() ?? [];
    return projects.map(p => ({
      id: p.projectId ?? p.id,
      firstLabel: p.projectCode ?? p.projectName ?? '',
      secondLabel: p.projectName ?? ''
    })) as DropdownData[];
  });

  readonly #clearProjectEffect = effect(() => {
    const currentProject = this.selectedProject();
    const availableProjects = this.groupedProjects();
    const salesOfficeId = this.selectedSalesOfficeId();

    // Only clear if a sales office is selected and the projects don't match
    if (
      currentProject &&
      salesOfficeId &&
      availableProjects.length > 0 &&
      !availableProjects.some(p => currentProject.includes(p.id))
    ) {
      this.selectedProject.set(null);
    }
  });
  readonly #holidayCalendarRequest = computed(() => ({
    projectIds: this.selectedProject() ?? this.allProjectIds() ?? [],
    year: this.currentYear(),
    startDate: this.viewStartDate(),
    endDate: this.viewEndDate()
    // isDownload: this.exportToPdf()
  }));
  #holidayCalendarResource = rxResourceFixed({
    params: () => this.#holidayCalendarRequest(),
    defaultValue: { leaveCalendar: [], lotClosingDates: [] } as HolidayCalendarSummary,
    stream: ({ params }) => {
      if (!params.projectIds || params.projectIds.length === 0) {
        return of({ leaveCalendar: [], lotClosingDates: [] } as HolidayCalendarSummary);
      }
      return this.#salesService.getHolidayCalendarSummary({
        projectIds: params.projectIds,
        year: params.year,
        startDate: params.startDate,
        endDate: params.endDate,
        isDownload: false
      });
    }
  });
  ytdActualClosingsResource = rxResourceFixed({
    params: () => ({ projectIds: this.selectedProject() ?? this.allProjectIds() ?? [] }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.projectIds || params.projectIds.length === 0) {
        return of([]);
      }
      return this.#salesService.getYtdActualClosings(params.projectIds);
    }
  });
  readonly #welcomeEmailStatusResource = rxResourceFixed({
    params: () => ({ dealId: this.selectedDealId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (!params.dealId) {
        return of(null);
      }
      return this.#salesService.getWelcomeEmailStatus(params.dealId);
    }
  });
  ytdActualClosings = computed(() => this.ytdActualClosingsResource.value() ?? []);
  actualClosings = computed(() => this.projectsWithLots().reduce((acc, curr) => acc + curr.actualCount, 0) ?? 0);
  targetClosings = computed(() => this.projectsWithLots().reduce((acc, curr) => acc + curr.targetCount, 0) ?? 0);
  totalClosings = computed(() => this.actualClosings() + this.targetClosings());
  readonly welcomeEmailStatus = computed(() => this.#welcomeEmailStatusResource.value()?.isWelcomeEmailSent ?? false);
  selectedClosingDate = signal<string | null>(null);
  markAsClosed(): void {
    if (!this.selectedDealId() || !this.form.value.closingDate) {
      return;
    }
    this.#salesService
      .markAsClosed(this.selectedDealId()!, format(this.form.value.closingDate, 'yyyy-MM-dd') ?? '')
      .subscribe({
        next: () => {
          this.#modalService.dismissAll();
          this.ytdActualClosingsResource.reload();
        }
      });
  }
  onExportPDF(): void {
    const body: HolidayCalendarSummaryParams = {
      projectIds: this.selectedProject() ?? this.allProjectIds() ?? [],
      startDate: this.viewStartDate(),
      year: this.currentYear(),
      endDate: this.viewEndDate(),
      isDownload: true
    };
    console.log('body', body);
    this.#salesService.generatePDF(body).subscribe();
  }
  readonly events = computed(() => {
    const calendarData = this.#holidayCalendarResource.value();
    if (!calendarData) {
      return [];
    }
    return [
      ...this.#mapLotClosingDatesToEvents(calendarData.lotClosingDates),
      ...this.#mapLeaveCalendarToEvents(calendarData.leaveCalendar)
    ];
  });

  readonly dealResource = rxResourceFixed<Deal[], { id: string | null }>({
    params: () => ({ id: this.selectedDealId() }),
    defaultValue: [],
    stream: () => this.#salesApsService.getDeals()
  });
  readonly selectedDeal = computed<Deal | null>(() => {
    const deals = this.dealResource.value() ?? [];
    const id = this.selectedDealId();
    if (!id) {
      return null;
    }
    return deals.find(d => d.id === id) ?? null;
  });

  readonly linkedSalesAgentsResource = rxResourceFixed<LinkedSalesAgentInfo[], { dealId: string }>({
    params: () => ({ dealId: this.selectedDeal()?.id ?? '' }),
    defaultValue: [],
    stream: ({ params }) => {
      if (params.dealId) {
        return this.#salesService.readLinkedSalesAgentsByLot(params.dealId);
      }
      return of([]);
    }
  });

  readonly holidays = computed(() => {
    const calendarData = this.#holidayCalendarResource.value();
    if (!calendarData?.leaveCalendar?.length) {
      return [];
    }
    return calendarData.leaveCalendar
      .filter(holiday => holiday.date && holiday.name)
      .map(holiday => ({
        name: holiday.name,
        date: this.#formatDateForDisplay(holiday.date)
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  });

  readonly displayYear = computed(() => this.currentYear());

  readonly hasProjects = computed(() => {
    const selected = this.selectedProject();
    const allProjectIds = this.allProjectIds();
    return (selected && selected.length > 0) ?? (allProjectIds && allProjectIds.length > 0);
  });

  readonly projectsWithLots = computed(() => {
    const lotClosingDates = this.#holidayCalendarResource.value()?.lotClosingDates ?? [];
    const map = new Map<string, { projectId: string; projectName: string; targetCount: number; actualCount: number }>();

    for (const d of lotClosingDates) {
      if (!d.projectId) continue;
      if (!map.has(d.projectId)) {
        map.set(d.projectId, {
          projectId: d.projectId,
          projectName: d.projectName,
          targetCount: 0,
          actualCount: 0
        });
      }
      const entry = map.get(d.projectId)!;
      if (d.actualClosingDate) {
        entry.actualCount += 1;
      } else if (d.targetClosingDate) {
        entry.targetCount += 1;
      }
    }
    return Array.from(map.values());
  });

  onViewDateChange(startDate: any): void {
    this.ytdActualClosingsResource.reload();
    const year = new Date(startDate).getFullYear();
    if (year !== this.currentYear()) {
      this.currentYear.set(year);
    }
  }

  #mapLotClosingDatesToEvents(lotClosingDates: LotClosingDate[]): CalendarEvent[] {
    if (!lotClosingDates?.length) {
      return [];
    }
    return lotClosingDates.flatMap(lotClosing => {
      const events: CalendarEvent[] = [];
      const projectColor = this.#getProjectColor(lotClosing.projectId);
      if (lotClosing.targetClosingDate) {
        events.push({
          ...this.#createClosingEvent(
            lotClosing.targetClosingDate,
            `Target Closing - Lot ${lotClosing.lotNo}`,
            lotClosing.projectName,
            projectColor
          ),
          border: 'white',
          borderColor: 'white',
          id: lotClosing.dealId
        });
      }
      if (lotClosing.actualClosingDate) {
        events.push({
          ...this.#createClosingEvent(
            lotClosing.actualClosingDate,
            `Actual Closing - Lot ${lotClosing.lotNo}`,
            lotClosing.projectName,
            projectColor
          ),
          border: '2px solid #FF0000',
          borderColor: '#FF0000',
          id: lotClosing.dealId
        });
      }
      return events;
    });
  }
  #mapLeaveCalendarToEvents(leaveCalendar: LeaveCalendarEvent[]): CalendarEvent[] {
    if (!leaveCalendar?.length) {
      return [];
    }
    return leaveCalendar
      .filter(leave => leave.date)
      .map(leave => ({
        title: leave.name || 'Holiday',
        start: this.#formatDate(leave.date),
        end: this.#formatDate(leave.date),
        border: 'white',
        borderColor: 'white'
      }));
  }
  #createClosingEvent(date: string, title: string, projectName: string, color?: string): CalendarEvent {
    const formattedDate = this.#formatDateToLocal(date);
    return {
      title: `${title} (${projectName})`,
      start: formattedDate,
      end: formattedDate,
      backgroundColor: color
    };
  }

  #getProjectColor(projectId: string): string {
    if (!this.projectColorMap.has(projectId)) {
      this.projectColorMap.set(projectId, this.#generateRandomColor());
    }
    return this.projectColorMap.get(projectId)!;
  }

  #generateRandomColor(): string {
    const randomColor = Math.floor(Math.random() * 0xffffff).toString(16);
    return `#${randomColor.padStart(6, '0')}`;
  }
  #formatDate(date: string): string {
    const d = new Date(date || '');
    return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
  }
  #formatDateToLocal(date: string): string {
    const d = new Date(date && !date.includes('T') ? `${date}T00:00:00Z` : date || '');
    return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
  }
  #isDifferentDate(date1: string | null, date2: string | null): boolean {
    if (!date1 || !date2) {
      return !!date1;
    }
    return this.#formatDate(date1) !== this.#formatDate(date2);
  }
  #formatDateForDisplay(date: string): string {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  colors = ['#dbe9ff', '#c5fce9ff', '#f7e5caff', '#d1d0ffff', '#ffd4dfff', '#c7dbf7ff', '#decefdff'];

  getColor(index: number): string {
    return this.colors[index % this.colors.length];
  }
  onEventClick(event: any): void {
    const check = this.ability.can('mark-deal-closed', 'calendar');
    if (check && event?.id) {
      this.selectedDealId.set(event?.id ?? null);
      this.openFullCalendarModal(event);
      this.closeMorePopover();
    }
  }
  openFullCalendarModal(event: any): void {
    const deal = this.selectedDeal();
    const dateStr = deal?.contract?.actualClosingDate ?? new Date().toISOString().split('T')[0];
    if (dateStr) {
      const d = new Date(dateStr);
      if (!isNaN(d.getTime())) {
        this.form.patchValue({ closingDate: new Date(d.toISOString().split('T')[0]) });
      }
    }
    this.#modalService.open(this.dealDetailsModal, this.#ngbModalService.centerModal);
  }
  closeMorePopover(): void {
    document.querySelector<HTMLButtonElement>('.fc-popover .fc-popover-close')?.click();
  }
}
